<template>
  <div class="w-full clearfix">
    <ag-grid-vue
      :rowHeight="gridOptions.rowHeight || DEFAULT_ROW_HEIGHT"
      serverSideStoreType="partial"
      :key="key"
      :style="{height}"
      class="ag-theme-alpine"
      :gridOptions="gridOptions"
      enableCellTextSelection="true"
      :modules="modules"
      @grid-ready="onGridReady"
      @modelUpdated="calculateHeight"
      @selectionChanged="handleSelectionChanged"
      @columnVisible="handleColumnStateChange"
      @sortChanged="handleSortChanged"
      @paginationChanged="paginationChanged">
    </ag-grid-vue>
    <div class="pagination-bar" v-if="pager.totalItems > (perPageDefault || 10)">
      <div class="page-sizes clearfix">
        <span>Show rows</span>
        <v-select class="page-sizes-menu" :options="pageSizes" v-model="perPage"></v-select>
      </div>

      <div class="links clearfix">
        <button @click="setPage(1)" class="first" :class="{'disabled': pager.currentPage === 1}">
          <span class="speakol speakol-double-arrow-left"></span>
        </button>
        <ul class="page-numbers">
          <li class="page-item previous" :class="{'disabled': pager.currentPage === 1}">
            <a class="page-link" @click="setPage(pager.currentPage - 1)">
              <span class="speakol speakol-arrow-left"></span>
            </a>
          </li>

          <li v-for="page in pager.pages" :key="page" class="page-item page-number"
              :class="{'active': pager.currentPage === page}">
            <a class="page-link" @click="setPage(page)">{{ page }}</a>
          </li>

          <li class="page-item next" :class="{'disabled': pager.currentPage === pager.totalPages}">
            <a class="page-link" @click="setPage(pager.currentPage + 1)">
              <span class="speakol speakol-arrow-right"></span>
            </a>
          </li>
        </ul>
        <button @click="setPage(pager.totalPages)" class="last"
                :class="{'disabled': pager.currentPage === pager.totalPages}">
          <span class="speakol speakol-double-arrow-right"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import '@ag-grid-community/core/dist/styles/ag-grid.css'
import '@ag-grid-community/core/dist/styles/ag-theme-alpine.css'
import {LicenseManager} from '@ag-grid-enterprise/core'
import {ColumnsToolPanelModule} from '@ag-grid-enterprise/column-tool-panel'
import {AgGridVue} from '@ag-grid-community/vue'
import {ServerSideRowModelModule} from '@ag-grid-enterprise/server-side-row-model'
import {RowGroupingModule} from '@ag-grid-enterprise/row-grouping'
import paginate from 'jw-paginate'
import AgPinnedGridFeatureAction from './AgPinnedGridFeatureAction'

LicenseManager.setLicenseKey('CompanyName=Speakol DMCC,LicensedApplication=Speakol,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=1,AssetReference=AG-022384,ExpiryDate=15_December_2022_[v2]_MTY3MTA2MjQwMDAwMA==2cb5088325d38cc3200640c34c3a1edc')

export default {
  name: 'a-g-speakol-table',
  data () {
    return {
      key: Math.random(),
      pageSizes: [10, 25, 50, 100],
      pager: {},
      perPage: 10,
      maxPages: 10,
      totalItems: 100,
      currentPage: 1,
      modules: [ServerSideRowModelModule, RowGroupingModule, ColumnsToolPanelModule],
      api: null,
      columnsApi: null,
      calculatedHeight: 120,
      DEFAULT_ROW_HEIGHT: 56
    }
  },
  components: {
    AgGridVue,
    AgPinnedGridFeatureAction
  },
  props: {
    shouldClearSelection: {
      type: Boolean,
      default: true
    },
    filters: {
      required: false,
      type: Object,
      default () {
        return {}
      }
    },
    columns: {
      required: true,
      type: Array
    },
    perPageDefault: {
      type: Number,
      required: false
    },
    options: {
      required: false,
      type: Object,
      default () {
        return {}
      }
    }
  },
  created () {
    if (this.perPageDefault) {
      this.perPage = this.perPageDefault
    }
  },
  updated () {
    this.handleColumnStateChange()
  },
  computed: {
    height () {
      return `${this.calculatedHeight}px`
    },
    gridOptions () {
      // merge default Options with user options
      return Object.assign({
        alwaysShowVerticalScroll: true,
        alwaysShowHorizontalScroll: true,
        suppressRowClickSelection: true,
        suppressColumnVirtualisation: true,
        suppressMultiSort: true,
        suppressPaginationPanel: true,
        suppressContextMenu: true,
        suppressHorizontalScroll: false,
        suppressCellSelection: true,
        rowBuffer: 100,

        overlayNoRowsTemplate: this.overlayNoRowsTemplate(),
        enableCellChangeFlash: true,
        defaultColDef: {
          flex: 1,
          resizable: true,
          suppressMenu: true,
          unSortIcon: true,
          suppressMovable: true,
          cellRendererSelector (params) {
            if (params.node.rowPinned) {
              return {
                component: 'AgPinnedGridFeatureAction'
              }
            } else {
              return undefined
            }
          }
        },
        columnDefs: this.columns,
        rowModelType: 'serverSide',
        paginationPageSize: this.perPage,
        cacheBlockSize: 10,
        pagination: true,
        animateRows: true,
        headerHeight: 60,
        // rowHeight: 56,
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressSideButtons: true,
                suppressColumnFilter: true,
                suppressColumnSelectAll: true,
                suppressColumnExpandAll: true
              }
            }
          ]
        }
      }, this.options)
    }
  },
  watch: {
    totalItems () {
      this.initPager()
    },
    perPage () {
      if (this.api) {
        this.options = {
          ...this.options,
          cacheBlockSize: this.perPage || this.perPageDefault || 10,
          paginationPageSize: this.perPage || this.perPageDefault || 10
        }

        this.reloadGrid()
        this.initPager()
      }
    }
  },
  methods: {
    handleColumnStateChange() {
      this.$emit('visibleColumnsChanged', this.columnsApi.getColumnState().filter(column => !column.hide))
    },

    handleSelectionChanged (params) {
      this.$emit('selectionChanged', params.api.getSelectedNodes().map((node) => {
        return node.data
      }))
    },

    handleSortChanged () {
      const colState = this.columnsApi.getColumnState()

      const sortState = colState
        .filter(function (s) {
          return s.sort !== null
        })
        .map(function (s) {
          return { colId: s.colId, sort: s.sort }
        })

      this.$emit('sortChanged', sortState)
    },

    clearSelection () {
      if (this.api) {
        this.api.deselectAll()
      }
    },

    overlayNoRowsTemplate () {
      return '<span class="table-empty-state ag-overlay-loading-center" style="margin-top: 65px !important; padding: 6px 20px;">No Results Found</span>'
    },

    setupResponsive () {
      this.initColumnResponsive()
      this.handleResponsiveCallbacks(window.matchMedia('(max-width: 767px)'), this.initColumnResponsive)
      this.handleResponsiveCallbacks(window.matchMedia('(min-width: 768px)'), this.resetColumnsState)
      this.handleResponsiveCallbacks(window.matchMedia('(min-width: 1024px)'), this.resetColumnsState)
      this.handleResponsiveCallbacks(window.matchMedia('(min-width: 1280px)'), this.resetColumnsState)
    },

    handleResponsiveCallbacks (mediaQuery, callback) {
      if (mediaQuery.addEventListener) {
        return mediaQuery.addEventListener('change', callback)
      }

      if (mediaQuery.addListener) {
        return mediaQuery.addListener(callback)
      }
    },


    initColumnResponsive () {
      const screenWidth = screen.width
      if (screenWidth < 768) {
        this.unPinColumns()
      }
    },

    unPinColumns () {
      const columns = this.columnsApi.getColumnState()
      if (!columns) return
      const pinnedColumnsIds = columns.filter(column => column.pinned).map(column => column.colId)
      this.columnsApi.setColumnsPinned(pinnedColumnsIds, null)
    },

    resetColumnsState (event) {
      if (!event.matches) {
        return
      }

      this.columnsApi.resetColumnState()
    },

    onGridReady (params) {
      this.api = params.api

      this.columnsApi = params.columnApi

      this.api.setServerSideDatasource(this.gridOptions.dataSource(this.filters))

      this.initPager(1)

      this.setupResponsive()
    },

    initPager (pageNum) {
      this.pager = paginate(this.totalItems, pageNum || this.currentPage, this.perPage, this.maxPages)
    },

    setPage (pageNumber, force = false) {
      if (!force && this.currentPage === pageNumber) {
        return
      }

      this.currentPage = pageNumber
      this.api.paginationGoToPage(this.currentPage - 1)
      this.pager = paginate(this.totalItems, this.currentPage, this.perPage, this.maxPages)
    },

    paginationChanged (params) {
      this.$emit('paginationChanged')
      this.calculateHeight(params)
      // clear any selected data
      if (this.shouldClearSelection) {
        this.clearSelection()
      }
    },

    calculateHeight (params) {
      // set height = header + row * row_num
      // if not last page then rows_number = this.pageSizes
      // if last page then rows_number = totalItems % pageSize
      this.totalItems = params.api.paginationGetRowCount()

      const isLastPage = (params.api.paginationGetTotalPages() - 1) === params.api.paginationGetCurrentPage()
      const lastPageRows = this.totalItems % params.api.paginationGetPageSize()
      const dynamicRowsNumber = isLastPage ? (lastPageRows === 0 ? params.api.paginationGetPageSize() : lastPageRows) : this.totalItems ? params.api.paginationGetPageSize() : 0
      const pinnedRows = params.api.getPinnedTopRowCount()
      const calculatedRowNumber = dynamicRowsNumber + pinnedRows
      const displayedChildRowsWithoutPagination = 'paginateChildRows' in this.gridOptions && this.gridOptions.paginateChildRows === false ? params.api.getDisplayedRowCount() - calculatedRowNumber : 0
      const rowsNumber = calculatedRowNumber + displayedChildRowsWithoutPagination

      if (rowsNumber) {
        this.calculatedHeight = 80 + ((this.gridOptions.rowHeight || this.DEFAULT_ROW_HEIGHT) * rowsNumber)
      } else {
        this.calculatedHeight = 120
      }

      this.$emit('dataChange', {totalItems: this.totalItems})
    },

    setColumnDefs (columns) {
      this.api.setColumnDefs(columns)
    },

    refreshGrid () {
      this.api.purgeServerSideCache([])
    },

    applyFilters () {
      this.refreshGrid()
      this.setPage(1, true)
      this.setupResponsive()
    },

    reloadGrid () {
      this.key = Math.random()
    },

    getTotalRows () {
      return this.api.getDisplayedRowCount()
    },

    getItemsCount () {
      return this.totalItems
    }
  }
}
</script>

<style lang="scss" scoped>
$border-color: rgba(0, 0, 0, 0);

.ag-theme-alpine ::-webkit-scrollbar {
    width: 7px !important;
    height: 7px !important;
}
.ag-theme-alpine ::-webkit-scrollbar-track {
    border-radius: 4px !important;
}
.ag-theme-alpine ::-webkit-scrollbar-thumb {
    border-radius: 4px !important;
    background: gray;
}

/deep/ .ag-body-horizontal-scroll {
  pointer-events: all !important;
}

.pagination-bar {
  width: 100%;
}

.links,
.page-sizes {
  width: 100%;
}

.links {
  margin-top: 30px;

  span {
    color: black;
    width: 20px;
    height: 20px;
    display: inline-block;
    font-size: 16px;
    vertical-align: middle;
    font-weight: bold;
  }
}

.page-sizes span {
  display: inline-block;
  width: 90px;
}

.page-sizes .page-sizes-menu {
  display: inline-block;
  width: calc(100% - 90px);
}

.page-sizes /deep/ .vs__dropdown-toggle {
  height: 40px;
  border-radius: 20px;
}

.page-sizes /deep/ .vs__clear {
  display: none !important;
}

.pagination-bar {
  margin-top: 30px;
}

.first-last {
  border: 0;
  background: rgba(57, 176, 222, 0.1);
  border-radius: 20px;
  width: calc(25% - 10px);
  height: 40px;
  cursor: pointer;
  float: left;
}

.first {
  @extend .first-last;

  margin-right: 20px;
}

.last {
  @extend .first-last;

  margin-left: 20px;
}

.page-numbers {
  margin: 0;
  padding: 0;
  background: rgba(57, 176, 222, 0.1);
  float: left;
  border-radius: 20px;
  width: calc(50% - 20px);
}

.page-item {
  display: inline-block;

  a {
    width: 100%;
  }
}

.page-link {
  height: 40px;
  line-height: 40px;
  text-align: center;
  cursor: pointer;
  display: block;
}

.page-number {
  display: none;
}

.previous-next {
  width: 50%;
}

.previous {
  @extend .previous-next;

  border-radius: 20px;
}

.next {
  @extend .previous-next;

  border-radius: 20px;
}

.page-item a {
  color: #000;
}

.active {
  border-radius: 50%;
  background: #4492e7;
}

.active a {
  color: #fff;
}

.disabled {
  background: #f5f5f5;
}

.ag-theme-alpine {
  font-family: "Poppins", sans-serif !important;
  width: 100%;
  max-height: calc(100vh - 275px);
  // height: 100px !important;
}

.ag-theme-alpine /deep/ .ag-value-change-value-highlight {
  background: #def5e3 !important;
}

.ag-theme-alpine /deep/ .ag-cell-data-changed {
  background: #def5e3 !important;
}

.ag-theme-alpine /deep/ .ag-header {
  background-color: #f0f8fd;
  border-bottom-color: $border-color;
}

.ag-theme-alpine /deep/ .ag-root-wrapper {
  border-color: $border-color;
  cursor: unset;
}

.ag-theme-alpine /deep/ .ag-side-bar-left,
.ag-theme-alpine /deep/ .ag-side-bar-right {
  border-color: $border-color;
  background-color: #e3f4ff;
}

.ag-theme-alpine /deep/ .ag-row {
  border-color: $border-color;
}

.ag-theme-alpine /deep/ .ag-cell {
  display: flex;
  align-items: center;
}

.ag-theme-alpine /deep/ .ag-floating-top,
.ag-theme-alpine /deep/ .ag-row-pinned {
  height: 56px !important;
  min-height: 56px !important;
}

.ag-theme-alpine /deep/ .ag-row:nth-child(odd) {
  background: #fff;
}

.ag-theme-alpine /deep/ .ag-row:nth-child(even) {
  background: #f9fcfe;
}

.ag-theme-alpine /deep/ .ag-paging-panel {
  border-top-color: $border-color;
}

.ag-theme-alpine /deep/ .ag-row-hover {
  background: #f0f8fdde !important;
}

.ag-theme-alpine /deep/ .ag-cell {
  line-height: 56px;
}

.ag-theme-alpine /deep/ .ag-cell-wrapper > *:not(.ag-cell-value):not(.ag-group-value) {
  height: 56px;
}

.ag-theme-alpine /deep/ .ag-header-cell-text {
  font-size: 16px;
  font-weight: 600;
}

.ag-theme-alpine /deep/ .ag-overlay-loading-center {
  border: none;
  background: transparent;
  box-shadow: none;
  padding: 6px;
  outline: none;
  font-size: 14px;
  color: #707070;
  font-weight: 500;
}

.ag-theme-alpine /deep/ .ag-floating-top {
  border-bottom-color: #dadada;
}

// medium version
@media (min-width: 768px) {
  .ag-theme-alpine {
    max-height: calc(100vh - 220px);
  }

  .links {
    width: calc(60% - 10px);
    float: right;
    margin: 0;
  }

  .page-sizes {
    width: calc(40% - 10px);
    float: left;
  }
}

// large version
@media (min-width: 1024px) {
  .links {
    width: auto;
    max-width: 600px;
    margin-left: 30px;
  }

  .page-sizes {
    width: calc(20% - 10px);
  }

  .first-last {
    width: 40px;
  }

  .page-number {
    display: inline-block;
    width: 40px;
  }

  .previous-next {
    width: 40px;
  }

  .page-numbers {
    width: auto;
    max-width: 480px;
  }
}

// extra large version
@media (min-width: 1280px) {
  .pagination-bar {
    width: auto;
    max-width: 805px !important;
    float: right;
  }

  .page-sizes {
    width: 175px;
  }
}

@media (max-width: 576px) {
  .ag-theme-alpine /deep/ .ag-tool-panel-wrapper {
    width: 200px !important;
  }
}
</style>
